import {
  Pressable,
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
} from "react-native";
import { ms } from "./moderateScale";
import { Colors } from "@/constants/Colors";
import Icon from "./Icon";
import { formatDateTime } from "@/models/utils";
import { useUserSlice } from "@/models/useUserSlice";
import { useTranslation } from "react-i18next";
import Menu from "./Menu";
import { Note } from "@/models/types";

export default function NoteItem({
  noteId,
  onPress,
}: {
  noteId: string;
  onPress: () => void;
}) {
  const { t } = useTranslation();
  const note = useUserSlice((state) =>
    state.notes.find((note) => note.id === noteId)
  );

  if (!note) return null;

  const isPending = (
    [
      "upload_started",
      "upload_completed",
      "transcription_started",
      "transcription_completed",
      "summary_started",
    ] as Note["status"][]
  ).includes(note.status);
  const hasFailed = note.status.endsWith("failed");

  if (isPending) {
    note.emoji = "⏸️";
  }

  if (hasFailed) {
    note.emoji = "❌";
  }

  return (
    <Pressable onPress={onPress} style={styles.container}>
      <View style={styles.emojiContainer}>
        <Text style={{ fontSize: ms(16) }}>{note.emoji || "🍋"}</Text>
      </View>

      <View style={styles.contentContainer}>
        <Text numberOfLines={2} style={styles.title}>
          {note.name}
        </Text>
        {note.status === "summary_completed" && (
          <Text style={styles.date}>{formatDateTime(note.createdAt)}</Text>
        )}
        {isPending && (
          <View style={styles.pendingContainer}>
            <Text style={styles.pendingStatus}>{t("noteItem.pending")}</Text>
          </View>
        )}
        {hasFailed && (
          <Text style={styles.errorStatus}>
            {t("noteItem.transcriptionFailed") +
              (note.transcriptionError
                ? `: ${t(`noteItem.transcriptionError${note.transcriptionError}`)}`
                : "")}
          </Text>
        )}
      </View>

      <Menu
        actions={[
          {
            text: t("noteItem.renameNote"),
            onPress: () => useUserSlice.getState().note().rename(noteId),
          },
          {
            text: t("noteItem.deleteNote"),
            destructive: true,
            onPress: () => useUserSlice.getState().note().delete(noteId),
          },
        ]}
        actionComponent={
          <View
            style={{
              width: ms(48),
              height: ms(48),
              alignItems: "center",
              justifyContent: "center",
              marginRight: -ms(12),
            }}
          >
            <Icon icon="moreHorizontal" style={styles.icon} />
          </View>
        }
      />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {
    height: ms(90),
    justifyContent: "center",
    backgroundColor: Colors.white,
    paddingHorizontal: ms(20),
    borderRadius: ms(16),
    flexDirection: "row",
    alignItems: "center",
    gap: ms(16),
    marginBottom: ms(8),
  },
  pendingContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: ms(16),
  },
  contentContainer: {
    flex: 1,
  },
  title: {
    fontSize: ms(16),
    fontWeight: "400",
    marginBottom: ms(6),
  },
  date: {
    fontWeight: "300",
    fontSize: ms(12),
    color: Colors.subtitle,
  },
  icon: {
    width: ms(20),
    height: ms(20),
    tintColor: Colors.subtitle,
  },
  pendingStatus: {
    fontWeight: "300",
    fontSize: ms(12),
    color: Colors.warning,
  },
  errorStatus: {
    fontWeight: "300",
    fontSize: ms(12),
    color: Colors.error,
  },
  emojiContainer: {
    backgroundColor: Colors.text + "10",
    width: ms(40),
    height: ms(40),
    borderRadius: ms(32),
    alignItems: "center",
    justifyContent: "center",
  },
});
